import { defineStore } from 'pinia'
import AuthApi from '~/api/AuthApi'
import type { User } from '~/ts/interfaces/models/user.model.interface'
import type { AuthState, AuthUser } from '~/ts/interfaces/services/login.interface'
import { getAuthCookie, removeAuthCookie, setAuthCookie } from '~/utils/auth-cookie'
import { updateEchoAuthHeader } from '~/boot/providers/echo'
import {
  setLocale as setI18nLocale,
  hasExplicitLocaleSelection,
  clearExplicitLocaleSelection,
} from '~/boot/plugins/i18n'
import { clearAllPersistedLastActivity } from '~/utils/idle-activity'

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    authUser: null,
    bearerToken: getAuthCookie(),
    isLoggedIn: !!getAuthCookie(),
    termsVersion: 0,
    policyVersion: 0,
  }),

  actions: {
    async fetchAuthUser(): Promise<void> {
      await AuthApi.authUser()
        .then((response: User) => {
          this.authUser = response
          this.setLocale(response.setting?.settings.locale)
        })
        .catch(() => {
          this.resetAuthUser()
        })
    },

    async fetchTermsAndPolicyVersion(): Promise<void> {
      await AuthApi.termsAndPolicyVersion().then((response) => {
        this.termsVersion = response.terms_version
        this.policyVersion = response.policy_version
      })
    },

    setAuthUser(data: AuthUser): void {
      setAuthCookie(data.token)
      this.bearerToken = data.token
      this.isLoggedIn = !!data.token
      this.authUser = data.user
      this.setLocale(data.user.setting?.settings.locale)

      updateEchoAuthHeader(data.token)
    },

    // A profile with no locale (or an unsupported one) must apply nothing and
    // leave the boot plugin's detected locale in place — defaulting to 'nl' here
    // force-switched those users to Dutch on every reload. Persisting is left to
    // setI18nLocale so localStorage and the i18n instance cannot disagree.
    setLocale(locale?: string | null): void {
      // An explicit pre-auth choice (login-page toggle) must win over the
      // stored profile locale, otherwise it flips on login (#838). When the
      // user never touched the toggle, the profile locale applies as before.
      if (hasExplicitLocaleSelection()) return
      if (locale !== 'en' && locale !== 'nl') return

      setI18nLocale(locale)
    },

    resetAuthUser(): void {
      // Not keyed on this.authUser — resetAuthUser() also runs when
      // fetchAuthUser() fails, when there is no in-memory user to key on, and a
      // surviving stamp logs the same user straight back out on their next
      // login.
      clearAllPersistedLastActivity()
      clearExplicitLocaleSelection()

      this.authUser = null
      this.bearerToken = null
      this.isLoggedIn = false
      removeAuthCookie()
      updateEchoAuthHeader(null)
    },
  },
})
